// The MODDING panel of the hub (R229, R807, R815, R908).
//
// **The section shell** (`../sectionshell.ts`, ui-map-interface.md §16.16,
// card 01 of mockup round 092, R1902). Four sections, one card each:
// THEMES (USE A THEME), EFFECTS, WORKSHOP (MAKE A THEME) and MAP DESIGNER.
// Each rail row and tile states a live summary from the reads below, and
// none where the read has not answered. The theme preview left the USE
// card: it is the companion column PREVIEW of every section, with a caption
// that names the selected row. Under the room for the column it folds over
// the page as a band. A telephone pins the band over the THEMES and the
// EFFECTS page, and shows the preview under the tiles. A change of section
// clears the selected row, so the preview shows the look in force again.
//
// The shell writes the column and the folded band both, and the stylesheet
// shows one of them. The canvas stands in one place only, so `wire` puts
// the host into the slot that shows, and a `ResizeObserver` on the row
// moves it when the container query swaps the two.
//
// **The close-up** (R1914). The column holds a second preview under the
// full one: the map alone, with no chrome and no DOM card, nearer by
// `[presentation.menu] close_zoom`, at `close_frame_ms`. It shows the same
// row in the same look. The band and a telephone show the full preview
// alone, so the panel builds the close-up only while its slot in the
// column shows, and destroys it, context and all, when the container query
// hides the column.
//
// **USE A THEME** is the one place where a player picks a theme
// (themes.md §2, §2.1). It opens on THE WHOLE LOOK, then holds one block
// per theme category, in the order of `THEME_CATEGORIES` (R1909). A block
// shows every option at once: a chip with the swatches of its tokens for
// UI, a picture of the theme preview for WORLD, MARKS and CHART
// (`mods-pictures.ts`), a row with ▶ for MUSIC and SFX. The markup lives in
// `mods-looks.ts`.
//   - THE WHOLE LOOK sets WORLD, MARKS and CHART to one vendored level.
//   - Every player uses the card. An option the account cannot use —
//     CUSTOM, or a vendored entry with a tier mark — shows the tier tag and
//     stores nothing (R815). A vendored entry with no tier mark is free.
//   - CUSTOM opens a code field. `setThemeCode` looks the pair (code,
//     category) up, stores the pick and links the theme; the block then
//     shows `CUSTOM · <code>` and an overview of the theme. The field opens
//     pre-filled: with the stored code of the category, else with the
//     player's own code where the player made a theme of that category
//     (R819). UNSET CODE beside CHANGE THE CODE removes the code, the link
//     and the pick, and the block shows AUTO again.
//   - A suspended pick (R809) shows its stored value struck through, with
//     a note: the pick is kept, and the look shows AUTO.
//   - The blocks print the stored picks; the preview shows the look in
//     force, through `currentLook` and `lookToPreview` (R814).
//   - The Music block plays its playlist while it is selected, through the
//     shell's `musicSample`. A press on an SFX option plays one effect.
//
// **The preview canvas outlives the pane.** The shell rewrites the pane
// whenever the markup changes, so the preview lives in a host element the
// panel owns. The markup carries an empty slot, and `wire` moves the host
// into the new slot after every rewrite: a moved canvas keeps its WebGL2
// context. `close` and `destroy` take the preview down, so the hub never
// closes into a game with the preview's grid or context alive (R806).
//
// **EFFECTS** is where a player wears an effect grant (effects.md §3,
// R908, R909). Any tier picks, so the card wears no tier tag.
//   - The slot map (R1909): FLEETS with ALL HULLS and the twelve hulls,
//     TORPEDOES with ALL KINDS and the six kinds, and HOLD. Each slot
//     states its own pick, ↑ the pick of its parent, or nothing.
//   - WHAT FITS the picked slot: FOLLOW (a child slot) or NOTHING, every
//     held catalog row whose targets and scope fit, each with a picture
//     and WEAR, and the rows the account does not hold, with how a player
//     gets one. A press on such a row shows it in the preview and stores
//     nothing — as a tier-locked theme value does. A narrow pane opens
//     WHAT FITS as a sub-page of EFFECTS under ‹ EFFECTS, with one scroll,
//     and ‹ EFFECTS gives the slot map back at its place (R1919).
//   - A held row stores at once, through `ui/effectstate.ts`, which sends
//     the whole map of picks. The pick shows in the world after the next
//     join (R909), and the lede says so.
//
// **MAKE A THEME** lists the custom themes of the account
// (`GET /api/themes/mine`) and links the theme workshop page. It needs
// `theme_maker` (R808). The old `/api/mods/` route is gone (R815).
//
// **The map designer** is the static page `mapdesigner.html` (R603). It
// needs no key, and a map it writes is an Arcade map.

import { sellsNothing } from '../../../platform/platform';
import {
  KEY_PREMIUM, KEY_THEME_MAKER, KEY_UI_THEMES, THEME_CATEGORIES, THEME_CODE_ALPHABET,
  THEME_CODE_LEN, THEME_MARKS_FIELDS, THEME_UI_ALPHAS, THEME_UI_TOKENS, THEME_WORLD_FIELDS,
  type ThemeCategory,
} from '../../../gen/balance';
import {
  WHOLE_CATS, axisHtml, byLoudness, fillPictures, fitRowHtml, glyphBox, groupHtml,
  headHtml, legendHtml, levelWord, listRowHtml, mixFoldHtml, optionTile, picBox, slotHtml, swatchPic, wholeLevels,
  type OptionView,
} from './mods-looks';
import { PictureBaker, pictureKey, type Bakery, type PictureJob } from './mods-pictures';
import { button, previewTiles, tag, type Tile } from '../../choice/choice';
import {
  EFFECTS, EFFECT_SLOTS, KEYS, MENU_CLOSE_FRAME_MS, MENU_CLOSE_ZOOM, VENDORED_THEMES,
} from '../../../gen/presentation';
import { matches } from '../../../keys';
import { ShipClass, TorpedoKind } from '../../../gen/proto/aquaphobia';
import { effects as EF, hubMods as S } from '../../../gen/strings';
import { wordOf } from '../../../locale/wordof';
import { isoDate } from '../../../locale/stamp';
import { getMyThemes, messageOf, type AccountEffects, type MyThemes } from '../../../net/api';
import type { SfxId } from '../../../audio/triggers';
import {
  ThemePreview, PREVIEW_FOCI, type PreviewEffects, type PreviewFocus, type PreviewLook,
} from '../../../preview';
import { factionName } from '../../../world';
import { audioChoices } from '../../audiochoices';
import { MAP_DESIGNER_HREF, THEMES_HREF } from '../../hublink';
import { grantOf, isGranted, lastEffects, loadEffects, pickOf, setPick } from '../../effectstate';
import { cachedTheme, type CachedTheme } from '../../lookcache';
import {
  PICK_FIELD, lookToPreview, readChartBody, readMarksBody, readMusicBody, readSfxBody,
  readWorldBody, readUIBody, type Look,
} from '../../look';
import { CUSTOM, DEFAULT_SETTINGS, loadSettings, type GameSettings } from '../../settings';
import {
  ThemeCodeError, currentLook, setThemeCode, setThemePick, storedThemePick, unsetThemeCode,
} from '../../themestate';
import { THEME_CHOICES, resolveTheme, themeBody, type ThemeChoice } from '../../theme';
import { lightBodyFor } from '../../lightchrome';
import { LIGHT } from '../../../identity/pick';
import { TTL_ON_OPEN, hasKey, heldKeys, hubFaction } from '../hubstate';
import {
  ctlRow, empty, errLine, esc, h1, lede, linkRow, lockedTag, pending, plain, readable,
  rows, section, tierTag, wireOnce, type Panel, type PanelContext, type Row,
} from '../panel';
import {
  SectionNav, shellHtml, shellPress, type Companion, type Fold, type ShellPage, type ShellSection,
} from '../sectionshell';

/** The four sections of MODDING, in the rail order (§16.16). The ids are
 *  the address words: `?tab=mods&section=effects`. */
export const MODS_SECTIONS = ['themes', 'effects', 'workshop', 'designer'] as const;
type ModsSection = (typeof MODS_SECTIONS)[number];

/** The old words of a section: the names of the cards it holds. */
// compat(R1902, 2027-01-08)
const MODS_ALIASES: Readonly<Record<string, ModsSection>> = {
  use: 'themes', make: 'workshop', mapdesigner: 'designer',
};

/** The press of a rail row and of a tile, and ‹ MODDING of a narrow page. */
const GO = 'mods-sec';
const BACK = 'mods-back';
const SHELL = { go: GO, back: BACK };

/** A stored pick that is the default of its category: the vendored AUTO
 *  (`auto`) or the audio default (''). */
function isAuto(pick: string): boolean {
  return pick === '' || pick === 'auto';
}

/** The effect a press on the SFX row plays: one cue every SFX theme maps. */
const SFX_SAMPLE: SfxId = 'sfx-torpedo-fire';

/** The cache cell of the MAKE card. */
const MINE_CELL = 'mods.mine';

/** The name and the line of each category row. The MAKE card reads it by
 *  the category a theme record carries, so the table is a `Map` (R1341). */
const CATEGORY_WORDS: ReadonlyMap<string, [name: string, desc: string]> = new Map([
  ['ui', [S.cat_ui, S.desc_ui]],
  ['music', [S.cat_music, S.desc_music]],
  ['sfx', [S.cat_sfx, S.desc_sfx]],
  ['world', [S.cat_world, S.desc_world]],
  ['marks', [S.cat_marks, S.desc_marks]],
  ['chart', [S.cat_chart, S.desc_chart]],
]);

/** The preview a row moves to when no field of an overview is selected.
 *  MARKS moves to what its pick changes (`marksFocusOf`, R1915); the entry
 *  here is the frame of both marks. */
const CATEGORY_FOCUS: Record<ThemeCategory, PreviewFocus | null> = {
  ui: 'ui', music: null, sfx: null, world: 'spawn', marks: 'holdHulls', chart: 'chart',
};

/** What the picture of a WORLD and a CHART option shows (R1909). WORLD
 *  shows the round in flight and not the arrival, because a still of the
 *  arrival shows little. A MARKS picture shows what its option changes
 *  (`marksPictureFocus`, R1915). */
const PICTURE_FOCUS = { world: 'torpedo', chart: 'chart' } as const;

/** The MARKS fields a hull wears beside the ship icon set and its per-hull
 *  overrides (every field whose name starts with `ship`): the own and the
 *  squad mark and the two readouts. */
const HULL_MARKS: ReadonlySet<string> = new Set(['ownFleet', 'squadFleet', 'shield', 'digits']);
/** The MARKS fields a hold wears. */
const HOLD_MARKS: ReadonlySet<string> = new Set(['hold', 'ownHold', 'squadHold']);
/** The MARKS fields that are a focus of their own. */
const OWN_FOCUS_MARKS: Readonly<Record<string, PreviewFocus>> = {
  structure: 'structure', deposit: 'deposit', mapMarker: 'mapMarker', poi: 'poi',
};

/** The MARKS fields where `body` differs from the vendored AUTO. */
export function changedMarks(body: Readonly<Record<string, string>>): string[] {
  const auto = VENDORED_THEMES.marks.find((e) => e.id === 'auto') as Readonly<Record<string, string>> | undefined;
  return Object.keys(THEME_MARKS_FIELDS)
    .filter((f) => body[f] !== undefined && auto !== undefined && body[f] !== auto[f]);
}

/**
 * The focus that shows a set of changed MARKS fields (R1915): a hull mark
 * shows a hull (`ship`), a hold mark the hold (`hold`), both the own hold
 * and the hulls beside it (`holdHulls`). A change of one other field shows
 * that field's own focus. Null where nothing, or more than one other
 * field, changed.
 */
export function marksFocusOf(changed: readonly string[]): PreviewFocus | null {
  const hull = changed.some((f) => f.startsWith('ship') || HULL_MARKS.has(f));
  const hold = changed.some((f) => HOLD_MARKS.has(f));
  if (hull && hold) return 'holdHulls';
  if (hull) return 'ship';
  if (hold) return 'hold';
  const own = [...new Set(changed.map((f) => OWN_FOCUS_MARKS[f]).filter((f) => f !== undefined))];
  return own.length === 1 ? own[0] : null;
}

/** The frame of the MARKS scale: what the vendored options change between
 *  them, so AUTO, which changes nothing, shows the frame its neighbours show. */
function marksScaleFocus(): PreviewFocus {
  const all = new Set<string>();
  for (const e of VENDORED_THEMES.marks) for (const f of changedMarks(e as Readonly<Record<string, string>>)) all.add(f);
  return marksFocusOf([...all]) ?? 'holdHulls';
}

/** What the picture of one vendored MARKS option shows (R1915). */
export function marksPictureFocus(id: string): PreviewFocus {
  const e = VENDORED_THEMES.marks.find((x) => x.id === id) as Readonly<Record<string, string>> | undefined;
  return (e === undefined ? null : marksFocusOf(changedMarks(e))) ?? marksScaleFocus();
}

/** What THE WHOLE LOOK shows, in its pictures and in the preview: the
 *  widest frame, where the terrain, the marks and the chart pieces show. */
const WHOLE_FOCUS: PreviewFocus = 'chart';

/** How many rows of NOT HELD stand before + MORE. */
const FITS_SHOWN = 6;

/** The swatches of a UI option: the panel, the accent and the text token
 *  of the body the option wears — the vendored tokens, or the faction
 *  theme the choice resolves to, as `ui/look.ts uiBody` resolves it. In
 *  the light identity they are the tokens of its light body, the body the
 *  chrome then wears (R3321). */
function swatchesOf(id: string, faction: number): string[] {
  const vendored = VENDORED_THEMES.ui.find((e) => e.id === id);
  const choice = (THEME_CHOICES as readonly ThemeChoice[]).find((t) => String(t) === id) ?? 'auto';
  const dark = vendored
    ? { ...vendored.tokens, ...vendored.alphas }
    : themeBody(resolveTheme(choice, faction));
  const b = LIGHT ? lightBodyFor(dark) : dark;
  return [b.panel, b.accent, b.text];
}

// --- the EFFECTS card -------------------------------------------------------

/** The cache cell of the EFFECTS card. */
const EFFECTS_CELL = 'mods.effects';

/** The three parent slots, in map order (effects.md §2). */
const EFFECT_ROWS = ['fleet', 'torpedo', 'hold'] as const;
export type EffectTarget = (typeof EFFECT_ROWS)[number];

/** The words of each parent slot: its name on the map, the line of WHAT
 *  FITS, and the scope word of a row that fits every child. */
const EFFECT_WORDS: Record<EffectTarget, { slot: string; lede: string; scope: string }> = {
  fleet: { slot: S.slot_fleet, lede: S.fits_lede_fleet, scope: S.scope_fleet },
  torpedo: { slot: S.slot_torpedo, lede: S.fits_lede_torpedo, scope: S.scope_torpedo },
  hold: { slot: S.slot_hold, lede: S.fits_lede_hold, scope: S.scope_hold },
};

/** The map name of one slot: ALL HULLS, or the hull or the kind. */
export function slotLabel(slot: string): string {
  const kid = slot.split(':')[1];
  return kid === undefined ? EFFECT_WORDS[targetOf(slot)].slot : readable(kid);
}

/** What the preview looks at for one row. */
const EFFECT_FOCUS: Record<EffectTarget, PreviewFocus> = {
  fleet: 'effectFleet', torpedo: 'effectTorpedo', hold: 'effectHold',
};

/** The target of one slot: `fleet:Vedette` is a fleet slot. */
export function targetOf(slot: string): EffectTarget {
  return slot.split(':')[0] as EffectTarget;
}

/** The protobuf enum that names the scope of a target, and the prefix of
 *  its member names: ShipClass for a fleet, TorpedoKind for a torpedo. A
 *  hold has no child slot, so it has no scope enum. The enum object maps
 *  each name to its number and each number to its name. */
const SCOPE_ENUM: Record<EffectTarget, { table: Record<string | number, string | number>; prefix: string } | null> = {
  fleet: { table: ShipClass as unknown as Record<string | number, string | number>, prefix: 'SHIP_CLASS_' },
  torpedo: { table: TorpedoKind as unknown as Record<string | number, string | number>, prefix: 'TORPEDO_KIND_' },
  hold: null,
};

/**
 * The scope number of one slot: the ShipClass or the TorpedoKind the slot
 * names, and 0 for a parent slot (R908).
 *
 * The catalog states a scope as the protobuf enum NUMBER, so this reads the
 * number out of the enum and mirrors no table of its own.
 */
export function scopeOf(slot: string): number {
  const [target, id] = slot.split(':');
  const e = SCOPE_ENUM[target as EffectTarget];
  if (id === undefined || !e) return 0;
  const n = e.table[e.prefix + id.toUpperCase()];
  return typeof n === 'number' ? n : 0;
}

/** The child slots of one parent slot, in the catalog's own order. */
export function childSlots(target: EffectTarget): string[] {
  return (EFFECT_SLOTS as readonly string[]).filter((s) => s.startsWith(`${target}:`));
}

/** One effect a slot can wear. `code` is '' for NONE. */
export interface EffectValue {
  code: string;
  label: string;
  wire: number;
}

/** A sentence of the effect catalog, by code (`[ui.effects.text]`). */
function effectText(code: string, part: 'name' | 'desc' | 'how'): string {
  return wordOf(EF, `${code}_${part}`, code.toUpperCase());
}

/**
 * The effects that fit one slot: NONE, then every catalog row whose
 * targets and scope fit the slot (effects.md §3).
 *
 * A row with no scope fits the parent slot AND every child slot of its
 * target. A scoped row fits its own slot alone, because the parent slot
 * would put the effect on hulls it was never drawn for.
 */
export function effectValues(slot: string): EffectValue[] {
  const target = targetOf(slot);
  const scope = scopeOf(slot);
  const list: EffectValue[] = [{ code: '', label: S.value_none, wire: 0 }];
  for (const row of EFFECTS) {
    if (!(row.targets as readonly string[]).includes(target)) continue;
    const rowScope = target === 'torpedo' ? row.torpedo : row.hull;
    if (rowScope !== 0 && rowScope !== scope) continue;
    list.push({ code: row.code, label: effectText(row.code, 'name'), wire: row.wire });
  }
  return list;
}

/** The words for one refused pick. The server writes the sentence, because
 *  no screen switches on an effect refusal code. */
function effectRefusalText(err: unknown): string {
  return S.effects_failed({ message: messageOf(err) });
}

/** One transient state of an effect row. Nothing here is stored. */
interface EffectRowState {
  /** A locked effect the slot shows but did not store. */
  shown?: string;
  error: string;
}

/** One option of a category. `keys` are the entitlement keys it needs. */
export interface StepValue {
  id: string;
  label: string;
  keys: string[];
}

/** What the panel needs of a preview. `ThemePreview` is the shipped one. */
export interface PreviewHandle {
  show(focus: PreviewFocus, look: PreviewLook): void;
  pause(): void;
  resume(): void;
  destroy(): void;
}

export interface ModsPanelOpts {
  /** Builds the preview in the host: the full one, or the close-up of the
   *  map alone when `close` is true (R1914). A test hands over a recorder. */
  makePreview?: (host: HTMLElement, close: boolean) => PreviewHandle;
  /** Builds the baker of the option pictures (R1909). A test hands over
   *  a recorder. */
  makeBakery?: () => Bakery;
}

/** The options of one category, CUSTOM last (themes.md §2). */
export function stepValues(c: ThemeCategory): StepValue[] {
  const custom: StepValue = { id: CUSTOM, label: S.value_custom, keys: [KEY_UI_THEMES] };
  const auto: StepValue = { id: '', label: S.value_auto, keys: [] };
  switch (c) {
    case 'ui':
      // The faction themes are free (R807). The vendored UI themes follow
      // them, and each one needs its own tier mark alone (themes.md §4.2).
      return [
        ...THEME_CHOICES.map((t) => ({
          id: String(t),
          label: t === 'auto' ? S.value_auto : factionName(t).toUpperCase(),
          keys: [],
        })),
        ...VENDORED_THEMES.ui.map((e) => ({
          id: e.id, label: readable(e.id), keys: e.key ? [e.key] : [],
        })),
        custom,
      ];
    case 'music':
    case 'sfx': {
      // '' is the manifest's own default. A keyed entry needs its key.
      const a = audioChoices();
      const list = c === 'music' ? a.playlists : a.themes;
      return [
        auto,
        ...list.map((e) => ({ id: e.id, label: e.title.toUpperCase(), keys: e.key ? [e.key] : [] })),
        custom,
      ];
    }
    default: {
      // A vendored World, Marks or Chart entry needs its own tier mark
      // alone, as a playlist does (themes.md §2, §4.2).
      const vendored = VENDORED_THEMES[c] as readonly { id: string; key: string }[];
      return [
        ...vendored.map((e) => ({
          id: e.id,
          label: e.id === 'auto' ? S.value_auto : readable(e.id),
          keys: e.key ? [e.key] : [],
        })),
        custom,
      ];
    }
  }
}

/** The tier a tag names for a set of keys. */
function tierFor(keys: string[]): 'supporter' | 'premium' {
  return keys.includes(KEY_PREMIUM) || keys.includes(KEY_THEME_MAKER) ? 'premium' : 'supporter';
}

/** The words for one refused code (themes.md §11.2). */
export function refusalText(err: unknown): string {
  if (!(err instanceof ThemeCodeError)) return S.err_failed({ message: String(err) });
  switch (err.reason) {
    case 'malformed': return S.err_malformed({ len: THEME_CODE_LEN, alphabet: THEME_CODE_ALPHABET });
    case 'not_found': return S.err_not_found;
    case 'entitlement': return S.err_entitlement;
    case 'rate_limited': return S.err_rate_limited;
    case 'unavailable': return S.err_unavailable;
    default: return S.err_failed({ message: err.message });
  }
}

/** A track title, from a manifest URL: `/audio/music/deep-drift.mp3?v=1` → DEEP DRIFT. */
function trackTitle(url: string): string {
  const base = url.split('?')[0].split('/').pop() ?? url;
  return readable(base.replace(/\.[a-z0-9]+$/i, ''));
}

/** One transient state of a row. Nothing here is stored. */
interface RowState {
  /** A value the block shows but did not store: a locked value, or CUSTOM
   *  while its code field is open. Undefined follows the stored pick. */
  shown?: string;
  /** The code field is open. */
  editing: boolean;
  /** What the player typed. It stays out of the markup (the shell's rule). */
  typed: string;
  busy: boolean;
  error: string;
}

function freshRow(): RowState {
  return { editing: false, typed: '', busy: false, error: '' };
}

function freshRows(): Record<ThemeCategory, RowState> {
  const list = THEME_CATEGORIES.map((c) => [c, freshRow()]);
  return Object.fromEntries(list) as unknown as Record<ThemeCategory, RowState>;
}

/** The name of the hull or the kind a scoped catalog row fits. */
function scopeName(target: EffectTarget, scope: number): string {
  const e = SCOPE_ENUM[target];
  const name = e ? e.table[scope] : undefined;
  return e && typeof name === 'string' ? readable(name.replace(e.prefix, '').toLowerCase()) : '';
}

export function createModsPanel(ctx: PanelContext, opts: ModsPanelOpts = {}): Panel {
  const makePreview = opts.makePreview ?? ((host: HTMLElement, close: boolean) => (close
    ? new ThemePreview(host, { mapOnly: true, zoom: MENU_CLOSE_ZOOM, maxFps: 1000 / MENU_CLOSE_FRAME_MS })
    : new ThemePreview(host)));
  const makeBakery = opts.makeBakery ?? (() => new PictureBaker());

  let rs = freshRows();
  let selected: ThemeCategory | null = null;
  /** The selected field of an overview, which is also a preview focus. */
  let field: string | null = null;
  let host: HTMLDivElement | null = null;
  let preview: PreviewHandle | null = null;
  /** The close-up of the column and its host, while its slot shows (R1914). */
  let closeHost: HTMLDivElement | null = null;
  let closePreview: PreviewHandle | null = null;
  let sampling = false;
  /** The effect slot the reader selected, or null while a theme row holds
   *  the preview. The two card's selections are exclusive: one preview. */
  let eslot: string | null = null;
  /** The level of THE WHOLE LOOK the reader pressed last, while it holds
   *  the preview; null otherwise. */
  let whole: string | null = null;
  /** The reader's press on MIX BY CATEGORY in this visit, or null: the
   *  fold then stands open (`mixShown`, R2154). */
  let mixOpen: boolean | null = null;
  /** NOT HELD of WHAT FITS shows every row. */
  let fitsAll = false;
  /** The transient state of one effect slot. */
  let ers = new Map<string, EffectRowState>();
  /** The baker of the option pictures, built on the first ask of a visit. */
  let bakery: Bakery | null = null;
  /** The root of the last render: where a baked picture goes. */
  let lastRoot: HTMLElement | null = null;
  /** The open section and the section the address named (§16.16). */
  const nav = new SectionNav({ ids: () => MODS_SECTIONS, first: () => 'themes', aliases: MODS_ALIASES });
  /** Watches the row of the box and the column, so the preview follows
   *  the container query from the column to the band and back. */
  let watch: ResizeObserver | null = null;

  // --- reading --------------------------------------------------------------

  function held(): Set<string> { return heldKeys(ctx.me()); }

  function allowed(v: StepValue, keys: Set<string> = held()): boolean {
    return v.keys.every((k) => keys.has(k));
  }

  /** The value the row shows: the transient one, else the stored pick. */
  function shownId(s: GameSettings, c: ThemeCategory): string {
    return rs[c].shown ?? storedThemePick(s, c);
  }

  /** How a stored or shown id reads on the row. */
  function labelOf(s: GameSettings, c: ThemeCategory, id: string, list: StepValue[]): string {
    if (id === CUSTOM) {
      const code = s.codes[c];
      return code ? S.value_custom_code({ code }) : S.value_custom;
    }
    // A vendored AUTO is stored as `auto`, and the audio default as ''.
    return list.find((v) => v.id === id)?.label ?? (isAuto(id) ? S.value_auto : readable(id));
  }

  // --- the preview and the sound ----------------------------------------------

  function focusFor(c: ThemeCategory | null, f: string | null): PreviewFocus | null {
    if (f && (PREVIEW_FOCI as readonly string[]).includes(f)) return f as PreviewFocus;
    if (c === null) return 'territory';
    // A hull field of the overview (`shipCorsair`) is no focus: it shows a
    // hull, as the workshop shows it (R835).
    if (c === 'marks') return (f === null ? null : marksFocusOf([f])) ?? marksRowFocus();
    return CATEGORY_FOCUS[c];
  }

  /** The settings of the draw that runs, read once by `html` (the client
   *  review of 2026-10-14, item 27); null outside a draw. */
  let drawn: GameSettings | null = null;

  /** The stored settings: the copy of the draw that runs, else a fresh
   *  read. A press reads fresh, because it may follow a write. */
  function settingsNow(): GameSettings {
    return drawn ?? loadSettings();
  }

  /** The focus of the MARKS block: what the stored pick changes, as its
   *  picture shows it (R1915). A CUSTOM pick reads its cached body. The
   *  preview shows the look in force, so the stored pick decides. */
  function marksRowFocus(): PreviewFocus {
    const s = settingsNow();
    const pick = storedThemePick(s, 'marks');
    if (pick !== CUSTOM) return marksPictureFocus(pick);
    const body = readMarksBody(cachedTheme(s.codes.marks, 'marks')?.body);
    return (body === null ? null : marksFocusOf(changedMarks(body as unknown as Record<string, string>)))
      ?? CATEGORY_FOCUS.marks ?? 'holdHulls';
  }

  /** The wire ids the preview stamps on the staged objects (R910). A
   *  selected slot wins over its parent, so a hull or a kind shows its own pick. */
  function previewEffects(): PreviewEffects {
    const wireOf = (slot: string): number => {
      const code = shownEffect(slot);
      return EFFECTS.find((e) => e.code === code)?.wire ?? 0;
    };
    const of = (t: EffectTarget): number =>
      (eslot !== null && targetOf(eslot) === t ? wireOf(eslot) : wireOf(t));
    return { fleet: of('fleet'), torpedo: of('torpedo'), hold: of('hold') };
  }

  /** Shows the selected row in the look in force, on the full preview and
   *  on the close-up, or on `only`. The Music and SFX rows leave the
   *  picture as it is: they sound instead. */
  function showPreview(only?: PreviewHandle): void {
    const to = only !== undefined ? [only] : [preview, closePreview];
    if (to.every((p) => p === null)) return;
    const focus = eslot !== null ? EFFECT_FOCUS[targetOf(eslot)]
      : whole !== null ? WHOLE_FOCUS : focusFor(selected, field);
    if (focus === null) return;
    const s = loadSettings();
    const look = { ...lookToPreview(currentLook(s), readingFor(s, focus), hubFaction(ctx.me())), effects: previewEffects() };
    for (const p of to) p?.show(focus, look);
  }

  /** Takes the close-up down: its loop, its frame and its context. */
  function dropClose(): void {
    closePreview?.destroy();
    closePreview = null;
    closeHost?.remove();
    closeHost = null;
  }

  function sampleMusic(on: boolean): void {
    if (on === sampling) return;
    sampling = on;
    ctx.musicSample?.(on);
  }

  function select(c: ThemeCategory, f: string | null = null): void {
    const moved = c !== selected || f !== field || eslot !== null || whole !== null;
    selected = c;
    field = f;
    eslot = null;
    whole = null;
    sampleMusic(c === 'music');
    if (c === 'sfx') ctx.play(SFX_SAMPLE);
    if (moved) showPreview();
  }

  // --- the changes ----------------------------------------------------------

  /** The account's own themes, where the MAKE card read them. Only a maker
   *  asks: the list is what the MAKE card shows. */
  function mine(): MyThemes | null {
    if (!hasKey(ctx.me(), KEY_THEME_MAKER)) return null;
    return ctx.data.cell<MyThemes>(MINE_CELL, TTL_ON_OPEN, getMyThemes).value ?? null;
  }

  /** What an opened code field starts with: the stored code of the
   *  category, else the player's own code where the player made a theme of
   *  that category (R819), else nothing. */
  function prefill(s: GameSettings, c: ThemeCategory): string {
    if (s.codes[c]) return s.codes[c];
    return mine()?.themes.find((t) => t.category === c)?.code ?? '';
  }

  /**
   * A press on one option. A free option stores at once; a locked option
   * shows its tier tag and stores nothing (R815); CUSTOM with no stored
   * code opens the code field. A press on the option already stored only
   * selects the block, so it sends no second link.
   */
  function choose(c: ThemeCategory, id: string): void {
    const s = loadSettings();
    const next = stepValues(c).find((v) => v.id === id);
    if (next === undefined) return;
    const r = rs[c];
    const stored = next.id === storedThemePick(s, c);
    r.error = '';
    field = null;
    if (!allowed(next)) {
      // A locked value shows its tier tag and stores nothing (R815).
      r.shown = next.id;
      r.editing = false;
      ctx.play('sfx-menu-error');
    } else if (!stored && next.id === CUSTOM && !s.codes[c]) {
      // CUSTOM with no code: the field opens, and the pick waits for a code.
      r.shown = CUSTOM;
      r.editing = true;
      r.typed = prefill(s, c);
      ctx.play('sfx-menu-press');
    } else {
      // The option already stored only selects the block.
      r.shown = undefined;
      r.editing = false;
      if (!stored) setThemePick(c, next.id);
      if (c !== 'sfx') ctx.play('sfx-menu-press');
    }
    selected = null; // `select` below counts this as a move
    select(c);
    ctx.refresh();
  }

  /** THE WHOLE LOOK: WORLD, MARKS and CHART take one vendored level. A
   *  category that holds it already sends nothing. The levels are free
   *  (R1231), so no key gates the press. */
  function chooseWhole(level: string): void {
    if (!wholeLevels().includes(level)) return;
    const s = loadSettings();
    for (const c of WHOLE_CATS) {
      rs[c] = freshRow();
      if (storedThemePick(s, c) !== level) setThemePick(c, level);
    }
    ctx.play('sfx-menu-press');
    holdWhole(level);
  }

  /** THE WHOLE LOOK holds the preview at `level`, or at its wide frame
   *  for '' (a press on its head): no row stays selected. */
  function holdWhole(level: string): void {
    selected = null;
    field = null;
    eslot = null;
    whole = level;
    sampleMusic(false);
    showPreview();
    ctx.refresh();
  }

  async function submit(c: ThemeCategory): Promise<void> {
    const r = rs[c];
    if (r.busy) return;
    r.busy = true;
    r.error = '';
    ctx.refresh();
    try {
      await setThemeCode(c, r.typed);
      r.editing = false;
      r.shown = undefined;
      r.typed = '';
      field = null;
      ctx.play('sfx-menu-press');
      showPreview();
      if (c === 'sfx') ctx.play(SFX_SAMPLE);
    } catch (err) {
      r.error = refusalText(err);
      ctx.play('sfx-menu-error');
    } finally {
      r.busy = false;
      ctx.refresh();
    }
  }

  /** UNSET CODE: the code, the link and the pick go, and the row shows the
   *  category's default vendored value (AUTO). */
  function unset(c: ThemeCategory): void {
    unsetThemeCode(c);
    rs[c] = freshRow();
    field = null;
    ctx.play('sfx-menu-press');
    showPreview();
    ctx.refresh();
  }

  function takeDown(): void {
    watch?.disconnect();
    watch = null;
    sampleMusic(false);
    bakery?.destroy();
    bakery = null;
    lastRoot = null;
    dropClose();
    preview?.destroy();
    preview = null;
    host = null;
    rs = freshRows();
    selected = null;
    field = null;
    eslot = null;
    whole = null;
    fitsAll = false;
    ers = new Map<string, EffectRowState>();
  }

  // --- the pictures (R1909) -------------------------------------------------

  /** The baker of this visit. A baked picture goes into the boxes of the
   *  last render at once, with no rewrite of the pane. */
  function baker(): Bakery {
    if (bakery === null) {
      const b = makeBakery();
      b.onBaked((key) => { if (lastRoot !== null) fillPictures(lastRoot, key); });
      bakery = b;
    }
    return bakery;
  }

  /** The chart reading a focus draws. The chart reading is a DISPLAY
   *  setting: with the chart off the pieces would not show, so the chart
   *  focus draws the default reading (R815). */
  function readingFor(s: GameSettings, focus: PreviewFocus): GameSettings['chart'] {
    return focus === 'chart' && s.chart === 'off' ? DEFAULT_SETTINGS.chart : s.chart;
  }

  /** What the canvas of a picture depends on in the settings: the picks of
   *  WORLD, MARKS and CHART, with the code of a CUSTOM pick and the keys
   *  that may suspend it. The UI pick and the faction colour the chrome
   *  alone, which no picture holds. */
  function lookParts(s: GameSettings): string[] {
    const parts = WHOLE_CATS.map((c) => {
      const p = storedThemePick(s, c);
      return p === CUSTOM ? `${p}:${s.codes[c]}` : p;
    });
    if (WHOLE_CATS.some((c) => storedThemePick(s, c) === CUSTOM)) parts.push([...held()].sort().join(','));
    return parts;
  }

  /** The settings with some picks of WORLD, MARKS and CHART changed. */
  function withPicks(s: GameSettings, picks: Partial<Record<(typeof WHOLE_CATS)[number], string>>): GameSettings {
    const next = { ...s } as Record<string, unknown>;
    for (const [c, id] of Object.entries(picks)) next[PICK_FIELD[c as ThemeCategory]] = id;
    return next as unknown as GameSettings;
  }

  /**
   * The box of one picture: the settings `next` at `focus`, with the
   * effects `fx` or none. It asks the baker, which builds the job only
   * where the picture is neither baked nor waiting, so the 4 Hz redraw
   * resolves no look for a picture it holds.
   */
  function picture(option: string, next: GameSettings, focus: PreviewFocus, fx?: PreviewEffects): string {
    const extra = fx === undefined ? '' : `${fx.fleet}/${fx.torpedo}/${fx.hold}`;
    const key = pictureKey(focus, readingFor(next, focus), extra, ...lookParts(next));
    baker().want(key, (): PictureJob => {
      const look = lookToPreview(currentLook(next), readingFor(next, focus), hubFaction(ctx.me()));
      return { focus, look: fx === undefined ? look : { ...look, effects: fx } };
    });
    return picBox(key, option);
  }

  // --- the EFFECTS card ---------------------------------------------------

  function erow(slot: string): EffectRowState {
    let r = ers.get(slot);
    if (!r) {
      r = { error: '' };
      ers.set(slot, r);
    }
    return r;
  }

  /** The code the slot shows: the locked one the reader pressed, else the
   *  pick. */
  function shownEffect(slot: string): string {
    return ers.get(slot)?.shown ?? pickOf(slot);
  }

  function eselect(slot: string): void {
    const moved = slot !== eslot || whole !== null;
    eslot = slot;
    selected = null;
    field = null;
    whole = null;
    sampleMusic(false);
    if (moved) showPreview();
  }

  /**
   * Sends the pick and puts the slot back when the server refuses.
   *
   * It sends on every press and holds no busy gate: the pick is local
   * first, so a reader who presses four rows must see four picks, and
   * `effectstate` keeps the newest write the truth.
   */
  async function store(slot: string, code: string): Promise<void> {
    const r = erow(slot);
    try {
      await setPick(slot, code);
    } catch (err) {
      r.error = effectRefusalText(err);
      ctx.play('sfx-menu-error');
    } finally {
      ctx.refresh();
    }
  }

  /**
   * A press on one row of WHAT FITS. A held effect, FOLLOW and NOTHING
   * store at once; an effect the account does not hold shows in the
   * preview with its LOCKED tag and stores nothing — the rule a
   * tier-locked theme value follows (R815).
   */
  function ewear(slot: string, code: string): void {
    const r = erow(slot);
    r.error = '';
    if (code !== '' && !isGranted(code)) {
      r.shown = code;
      ctx.play('sfx-menu-error');
    } else {
      r.shown = undefined;
      ctx.play('sfx-menu-press');
      if (code !== pickOf(slot)) void store(slot, code);
    }
    eslot = null; // `eselect` below counts this as a move
    eselect(slot);
    ctx.refresh();
  }

  function effectDetail(slot: string, v: EffectValue, locked: boolean): string {
    const r = erow(slot);
    const parts: string[] = [];
    if (v.code === '') parts.push(lede(S.none_note));
    else if (locked) parts.push(lockedTag(S.locked_tag) + lede(effectText(v.code, 'how')));
    else {
      parts.push(lede(effectText(v.code, 'desc')));
      const g = grantOf(v.code);
      if (g && g.expiresAt > 0) {
        const date = isoDate(g.expiresAt);
        parts.push(lede(S.expires_note({ date })));
      }
    }
    if (r.error !== '') parts.push(errLine(r.error));
    return `<div class="mdetail">${parts.join('')}</div>`;
  }

  /** One tile of the slot map. A child slot with no pick of its own wears
   *  the pick of its parent. */
  function slotTile(slot: string, wide: boolean, at: string): string {
    const t = targetOf(slot);
    const own = pickOf(slot);
    const up = slot === t ? '' : pickOf(t);
    const state = own !== '' ? 'own' : up !== '' ? 'fol' : 'non';
    const code = own !== '' ? own : up;
    return slotHtml({
      slot, label: slotLabel(slot), state, wears: code === '' ? '' : effectText(code, 'name'),
      on: slot === at, wide,
    });
  }

  /** WHERE YOU WEAR IT: the three groups of slots. */
  function slotMap(at: string): string {
    const groups = EFFECT_ROWS.map((t) => {
      const kids = childSlots(t);
      const head = t === 'fleet' ? S.group_fleet({ count: kids.length })
        : t === 'torpedo' ? S.group_torpedo({ count: kids.length }) : S.group_hold;
      return groupHtml(head, slotTile(t, true, at), kids.map((k) => slotTile(k, false, at)).join(''));
    });
    return `<div class="mslots">${legendHtml()}${groups.join('')}</div>`;
  }

  /** The line of a held row: the hulls or the kinds it fits, and how long
   *  the grant lasts. */
  function heldLine(t: EffectTarget, code: string): string {
    const row = EFFECTS.find((e) => e.code === code);
    const scope = row === undefined ? 0 : t === 'torpedo' ? row.torpedo : row.hull;
    const where = scope === 0 ? EFFECT_WORDS[t].scope : S.scope_only({ name: scopeName(t, scope) });
    const g = grantOf(code);
    const when = g !== null && g.expiresAt > 0 ? S.grant_ends({ date: isoDate(g.expiresAt) }) : S.grant_keeps;
    return `${where} · ${when}`;
  }

  /** The picture of one effect on its slot alone. */
  function effectPic(s: GameSettings, t: EffectTarget, v: EffectValue): string {
    const fx: PreviewEffects = { fleet: 0, torpedo: 0, hold: 0 };
    fx[t] = v.wire;
    return picture(`fx:${t}:${v.code}`, s, EFFECT_FOCUS[t], fx);
  }

  /** WHAT FITS one slot: the base row, the held effects and the effects the
   *  account does not hold, with how a player gets one, under the head of
   *  the list: the slot and the count of held and locked effects. */
  function fitsHtml(slot: string): string {
    const s = settingsNow();
    const t = targetOf(slot);
    const kid = slot !== t;
    const label = slotLabel(slot);
    const all = effectValues(slot);
    const mine = all.filter((v) => v.code !== '' && isGranted(v.code));
    const locked = all.filter((v) => v.code !== '' && !isGranted(v.code));
    const own = pickOf(slot);
    const shown = all.find((v) => v.code === shownEffect(slot)) ?? all[0];
    const up = pickOf(t);
    const parts: string[] = [
      `<div class="mfh"><b>${esc(S.fits_head({ slot: label }))}</b>`
        + `<i>${esc(S.fits_count({ held: mine.length, locked: locked.length }))}</i></div>`,
      lede(kid ? S.fits_lede_child({ slot: label, parent: EFFECT_WORDS[t].slot }) : EFFECT_WORDS[t].lede),
      effectDetail(slot, shown, shown.code !== '' && !isGranted(shown.code)),
      fitRowHtml({
        slot, code: '', on: own === '', locked: false, pic: glyphBox(kid ? '↑' : '—'),
        name: kid ? S.follow_row({ parent: EFFECT_WORDS[t].slot }) : S.nothing_row,
        line: !kid ? S.nothing_line
          : up === '' ? S.follow_wears_none : S.follow_wears({ name: effectText(up, 'name') }),
        end: own === '' ? `<em>${esc(S.in_use)}</em>` : tag(S.use, 'accent'),
      }),
      ...mine.map((v) => fitRowHtml({
        slot, code: v.code, on: own === v.code, locked: false, pic: effectPic(s, t, v),
        name: v.label, line: heldLine(t, v.code),
        end: own === v.code ? `<em>${esc(S.worn)}</em>` : tag(S.wear, 'accent'),
      })),
    ];
    if (locked.length > 0) {
      parts.push(`<div class="mgh">${esc(S.not_held_head)}</div>`);
      for (const v of fitsAll ? locked : locked.slice(0, FITS_SHOWN)) {
        parts.push(fitRowHtml({
          slot, code: v.code, on: shown.code === v.code, locked: true, pic: effectPic(s, t, v),
          name: v.label, line: effectText(v.code, 'how'), end: '',
        }));
      }
      if (locked.length > FITS_SHOWN) {
        const word = fitsAll ? S.fits_fewer : S.fits_more({ count: locked.length - FITS_SHOWN });
        parts.push(ctlRow(button(word, { act: 'efx-more' })));
      }
    }
    return `<div class="mfits">${parts.join('')}</div>`;
  }

  /** The EFFECTS page, with no box and no second title (R1945). It wears
   *  no tier tag: any tier picks (effects.md §3). A wide pane shows WHAT
   *  FITS beside the map, for the picked slot or for ALL HULLS; a narrow
   *  pane opens it as a sub-page (`sectionPage`). */
  function effectsCard(): string {
    const cell = ctx.data.cell<AccountEffects>(EFFECTS_CELL, TTL_ON_OPEN, loadEffects);
    if (cell.value === undefined) return plain(lede(S.effects_lede) + pending(cell));
    const wide = ctx.wide();
    const at = eslot ?? (wide ? EFFECT_ROWS[0] : '');
    return plain(lede(S.effects_lede)
      + `<div class="mfx">${slotMap(at)}${wide ? fitsHtml(at) : ''}</div>`);
  }

  /** WHAT FITS stands as the sub-page of EFFECTS: a slot is picked on a
   *  narrow pane, and the grants have answered. */
  function fitsPage(): boolean {
    return eslot !== null && !ctx.wide() && lastEffects() !== null;
  }

  // --- the USE card -----------------------------------------------------------

  /** The head of one category block: the name, the line and the value the
   *  block shows, struck through while suspended (R809). */
  function headOf(s: GameSettings, look: Look, c: ThemeCategory, keys: Set<string>): string {
    const list = stepValues(c);
    const id = shownId(s, c);
    const value = list.find((v) => v.id === id);
    const locked = rs[c].shown !== undefined && value !== undefined && !allowed(value, keys);
    const suspended = rs[c].shown === undefined && look.suspended.includes(c);
    const [name, desc] = CATEGORY_WORDS.get(c)!;
    return headHtml({
      act: 'sel', data: ` data-cat="${c}"`, sel: selected === c, name, line: desc,
      valueCls: locked ? ' locked' : suspended ? ' suspended' : '', value: labelOf(s, c, id, list),
    });
  }

  /** One option as the controls draw it. The stored pick wears ✓; an
   *  option the account cannot use names its tier. */
  function optionOf(s: GameSettings, c: ThemeCategory, v: StepValue, keys: Set<string>): OptionView {
    return {
      act: 'pick', data: { cat: c, id: v.id }, on: v.id === storedThemePick(s, c),
      locked: allowed(v, keys) ? '' : tierFor(v.keys),
      name: v.id === CUSTOM ? labelOf(s, c, CUSTOM, []) : v.label,
    };
  }

  /** The control of one category: preview tiles of the choice kit for UI
   *  (the swatches, a named group whose names wrap, R2169), WORLD, MARKS
   *  and CHART (the pictures), and a pick
   *  list with ▶ for MUSIC and SFX (R2011). While the category stores no
   *  code, CUSTOM is one ⌘ row under the control and not a tile with a
   *  glyph alone (R2154); its press opens the code field as before. */
  function controlOf(s: GameSettings, c: ThemeCategory, keys: Set<string>): string {
    const list = stepValues(c);
    const stored = storedThemePick(s, c);
    const label = CATEGORY_WORDS.get(c)?.[0] ?? '';
    const coded = Boolean(s.codes[c]);
    const custom = (v: StepValue): Tile => optionTile(optionOf(s, c, v, keys), swatchPic([], '⌘'));
    const customRow = (v: StepValue): string => listRowHtml({ ...optionOf(s, c, v, keys), sub: S.custom_line }, '⌘');
    const tail = list.filter((v) => v.id === CUSTOM);
    const rowTail = coded || tail.length === 0 ? '' : `<div class="mlist">${tail.map(customRow).join('')}</div>`;
    switch (c) {
      case 'ui': {
        const faction = hubFaction(ctx.me());
        const shown = coded ? list : list.filter((v) => v.id !== CUSTOM);
        return previewTiles(shown.map((v) => (v.id === CUSTOM ? custom(v)
          : optionTile(optionOf(s, c, v, keys), swatchPic(swatchesOf(v.id, faction))))),
        stored, 'pick', { label, named: true }) + rowTail;
      }
      case 'music':
      case 'sfx':
        return `<div class="mlist">${list.map((v) => (v.id === CUSTOM && !coded ? customRow(v)
          : listRowHtml(optionOf(s, c, v, keys)))).join('')}</div>`;
      default: {
        const focusOf = (id: string): PreviewFocus => (c === 'marks' ? marksPictureFocus(id) : PICTURE_FOCUS[c]);
        const tiles = byLoudness(list.filter((v) => v.id !== CUSTOM)).map((v) => optionTile(
          optionOf(s, c, v, keys),
          picture(`${c}:${v.id}`, withPicks(s, { [c]: v.id }), focusOf(v.id))));
        const codedTail = coded && tail.length > 0 ? previewTiles(tail.map(custom), stored, 'pick', { label }) : '';
        return previewTiles(tiles, stored, 'pick', { label }) + axisHtml() + rowTail + codedTail;
      }
    }
  }

  /** The level WORLD, MARKS and CHART all hold, or null while they hold
   *  two picks or a pick that is no level of `levels`. */
  function wholeLevelOf(s: GameSettings, levels: readonly string[]): string | null {
    const picks = WHOLE_CATS.map((c) => storedThemePick(s, c));
    return picks.every((p) => p === picks[0]) && levels.includes(picks[0]) ? picks[0] : null;
  }

  /** THE WHOLE LOOK: one picture per level, WORLD, MARKS and CHART at it. */
  function wholeHtml(s: GameSettings): string {
    const levels = wholeLevels();
    if (levels.length === 0) return '';
    const same = wholeLevelOf(s, levels);
    const head = headHtml({
      act: 'whole-sel', data: '', sel: whole !== null, name: S.whole_look, line: S.whole_lede,
      valueCls: '', value: same === null ? S.whole_mixed : levelWord(same),
    });
    const tiles: Tile[] = levels.map((level) => ({
      id: level, label: levelWord(level), sub: S.level_all({ value: readable(level) }),
      picture: picture(`whole:${level}`, withPicks(s, { world: level, marks: level, chart: level }), WHOLE_FOCUS),
    }));
    return `<div class="mblk mwhole">${head}`
      + `${previewTiles(tiles, same ?? '', 'whole', { label: S.whole_look })}</div>`;
  }

  /** What the selected row opened. */
  function detailHtml(s: GameSettings, look: Look, c: ThemeCategory, keys: Set<string>): string {
    const r = rs[c];
    const list = stepValues(c);
    const shown = list.find((v) => v.id === shownId(s, c));
    const parts: string[] = [];
    // A store app shows no way into the shop (R2927).
    const shop = sellsNothing() ? '' : ctlRow(button(S.see_the_shop, { act: 'go-shop' }, { variant: 'gold' }));
    const stored = storedThemePick(s, c);

    if (r.shown !== undefined && shown && !allowed(shown, keys)) {
      parts.push(tierTag(tierFor(shown.keys), false, ctx.me())
        + lede(S.locked_note({ pick: labelOf(s, c, stored, list) })) + shop);
    } else if (r.editing) {
      parts.push(lede(S.code_lede({ len: THEME_CODE_LEN }))
        + '<div class="form">'
        + `<input data-in="themecode-${c}" type="text" maxlength="${THEME_CODE_LEN}"`
        + ` autocapitalize="characters" autocomplete="off" spellcheck="false"`
        + ` placeholder="${esc(S.code_placeholder)}">`
        + '</div>'
        + ctlRow(button(r.busy ? S.code_busy : S.code_submit, { act: 'code', data: { cat: c } },
          { variant: 'primary', disabled: r.busy }))
        + errLine(r.error));
    } else {
      if (look.suspended.includes(c)) parts.push(lede(S.suspended_note) + shop);
      if (stored === CUSTOM && s.codes[c]) {
        const t = cachedTheme(s.codes[c], c);
        if (look.unresolved.includes(c) || !t || t.missing) {
          parts.push(lede(S.unresolved_note({ code: s.codes[c] })));
        }
        if (t) parts.push(overviewHtml(c, t));
        // A removal needs no key (R809), so UNSET CODE stands for a lapsed
        // account too; a new code needs `ui_themes`.
        parts.push(ctlRow(
          (keys.has(KEY_UI_THEMES) ? button(S.change_code, { act: 'change-code', data: { cat: c } }) : '')
          + button(S.unset_code, { act: 'unset-code', data: { cat: c } })));
      }
    }
    if ((c === 'music' || c === 'sfx') && !audioChoices().available) parts.push(lede(S.no_audio));
    else if (c === 'music') parts.push(lede(S.hint_music));
    else if (c === 'sfx') parts.push(lede(S.hint_sfx));
    return parts.length === 0 ? '' : `<div class="mdetail">${parts.join('')}</div>`;
  }

  /** One overview row that moves the preview, or plays a cue. */
  function pressRow(k: string, v: string, act: string, data: Record<string, string>, on: boolean): string {
    const attrs = Object.entries(data).map(([n, x]) => ` data-${n}="${esc(x)}"`).join('');
    return `<div class="row press${on ? ' sel' : ''}" data-act="${act}"${attrs} role="button" tabindex="0">`
      + `<span class="k">${esc(k)}</span><span class="v acc">${esc(v)}</span></div>`;
  }

  /** The settings overview of one custom theme (themes.md §2.1). */
  function overviewHtml(c: ThemeCategory, t: CachedTheme): string {
    const head: Row[] = [
      [S.ov_code, t.code, 'acc'],
      [S.ov_creator, t.owner?.name ?? S.ov_nobody],
      [S.ov_version, String(t.version)],
    ];
    let body = '';
    switch (c) {
      case 'ui': {
        const dark = readUIBody(t.body);
        if (!dark) break;
        // The light identity shows the light body the chrome wears (R3321).
        const b = LIGHT ? lightBodyFor(dark) : dark;
        const tokens = b as unknown as Record<string, string | number>;
        body = '<div class="swatches">' + THEME_UI_TOKENS.map((k) =>
          `<span class="swatch"><i style="background:${esc(String(tokens[k]))}"></i>`
          + `${esc(readable(k))}</span>`).join('') + '</div>'
          + rows(THEME_UI_ALPHAS.map((a): Row => [readable(a), String(tokens[a])]));
        break;
      }
      case 'music': {
        const b = readMusicBody(t.body);
        if (!b) break;
        body = rows([
          [S.ov_tracks, String(b.tracks.length)],
          [S.ov_shuffle, b.shuffle ? S.ov_on : S.ov_off],
          ...b.tracks.map((u, i): Row => [String(i + 1), trackTitle(u)]),
        ]);
        break;
      }
      case 'sfx': {
        const b = readSfxBody(t.body);
        if (!b) break;
        const effects = Object.keys(b.map).sort();
        const voices = Object.keys(b.voice.map).sort();
        body = rows([
          [S.ov_effects, effects.length === 0 ? S.ov_none : String(effects.length)],
        ]) + effects.map((id) => pressRow(readable(id), '♪', 'cue', { id }, false)).join('')
          + rows([[S.ov_voices, voices.length === 0 ? S.ov_none : voices.map(readable).join(', ')]]);
        break;
      }
      case 'world':
      case 'marks': {
        const b = c === 'world' ? readWorldBody(t.body) : readMarksBody(t.body);
        if (!b) break;
        const values = b as Record<string, string>;
        // A hull field that follows the ship icon set says nothing the `ship`
        // row does not say, so the overview lists an override only (R835).
        const fields = Object.keys(c === 'world' ? THEME_WORLD_FIELDS : THEME_MARKS_FIELDS)
          .filter((f) => !(f !== 'ship' && f.startsWith('ship') && values[f] === 'follow'));
        body = fields.map((f) =>
          pressRow(readable(f), readable(values[f] ?? ''), 'field', { cat: c, field: f }, field === f),
        ).join('');
        break;
      }
      case 'chart': {
        const b = readChartBody(t.body);
        if (!b) break;
        const lines: Row[] = [];
        for (const [slug, byReading] of Object.entries(b.pieces)) {
          for (const [reading, keys] of Object.entries(byReading ?? {})) {
            const on = (keys as { on?: boolean }).on;
            if (typeof on !== 'boolean') continue;
            lines.push([S.ov_piece({ piece: readable(slug), reading: readable(reading) }),
              on ? S.ov_on : S.ov_off]);
          }
        }
        body = rows(lines.length === 0 ? [[S.ov_pieces, S.ov_none]] : lines);
        break;
      }
    }
    return rows(head) + body;
  }

  /** True while MIX BY CATEGORY stands open: the reader's press of this
   *  visit, else open. The owner wants a reader to mix the categories and
   *  not only pick a whole look, so the six heads are in view on the
   *  first visit (R2154). */
  function mixShown(): boolean {
    return mixOpen ?? true;
  }

  /** The THEMES page: no box and no second title; the SUPPORTER tag
   *  stands in the page title (`useMark`, R1945). THE WHOLE LOOK stands
   *  open, and the six category blocks wait in MIX BY CATEGORY (R2154). */
  function useCard(): string {
    const s = settingsNow();
    const look = currentLook(s);
    const keys = held();
    const open = mixShown();
    const blocks = !open ? '' : THEME_CATEGORIES.map((c) => `<div class="mblk">${headOf(s, look, c, keys)}`
      + controlOf(s, c, keys) + (selected === c ? detailHtml(s, look, c, keys) : '') + '</div>').join('');
    return plain(lede(S.use_lede) + wholeHtml(s) + mixFoldHtml(open, blocks));
  }

  /** The tier tag of THEMES, at the right of the page title (R1945). */
  function useMark(): string {
    return tierTag('supporter', held().has(KEY_UI_THEMES), ctx.me());
  }

  // --- the MAKE card ----------------------------------------------------------

  /** The WORKSHOP page: no box and no second title; the PREMIUM tag
   *  stands in the page title (`makeMark`, R1945). */
  function makeCard(): string {
    if (!hasKey(ctx.me(), KEY_THEME_MAKER)) {
      return plain(
        `<div class="faded">${empty(S.make_no_key, S.make_no_key_why)}</div>`
        + (sellsNothing() ? '' : ctlRow(button(S.see_the_shop, { act: 'go-shop' }, { variant: 'gold' }))));
    }
    const cell = ctx.data.cell<MyThemes>(MINE_CELL, TTL_ON_OPEN, getMyThemes);
    const mine = cell.value;
    let body: string;
    if (mine === undefined) body = pending(cell);
    else if (mine.themes.length === 0) body = empty(S.make_empty, S.make_empty_why);
    else {
      // One code per creator (R819): the card names it once, and a row
      // names only its category, so a narrow row never cuts the code.
      const code = mine.code ?? mine.themes[0].code;
      body = rows([[S.make_code, code, 'acc']])
        + rows(mine.themes.map((t): Row => [
          CATEGORY_WORDS.get(t.category)?.[0] ?? readable(t.category),
          `${S.make_version({ version: t.version })} · ${S.make_links({ n: t.links })}`,
        ]));
    }
    return plain(lede(S.make_lede) + body + linkRow(THEMES_HREF, S.open_workshop));
  }

  /** The tier tag of WORKSHOP, at the right of the page title (R1945). */
  function makeMark(): string {
    return tierTag('premium', hasKey(ctx.me(), KEY_THEME_MAKER), ctx.me());
  }

  /** The MAP DESIGNER page: the steps and the link button, with no box and no
   *  second MAP DESIGNER title under the page title (R1945). The designer
   *  is free and static, so the page wears no tier tag and fades nothing.
   *  The button is an anchor and never an `act`, because the page opens in
   *  a tab of its own. */
  function designerCard(): string {
    return plain(
      lede(S.designer_lede)
      + lede(S.designer_step_1)
      + lede(S.designer_step_2)
      + lede(S.designer_step_3)
      + lede(S.designer_step_4)
      + lede(S.designer_limit)
      + linkRow(MAP_DESIGNER_HREF, S.open_designer));
  }

  function isCategory(v: string | undefined): v is ThemeCategory {
    return (THEME_CATEGORIES as readonly string[]).includes(v ?? '');
  }

  // --- the section shell (§16.16) -------------------------------------------

  /** The THEMES summary: how many categories hold a pick other than AUTO,
   *  and how many of them are CUSTOM. The picks are stored in this browser,
   *  so the figure is always known. */
  function themesSum(): string {
    const s = settingsNow();
    const picks = THEME_CATEGORIES.map((c) => storedThemePick(s, c));
    const changed = picks.filter((p) => !isAuto(p)).length;
    const custom = picks.filter((p) => p === CUSTOM).length;
    const total = THEME_CATEGORIES.length;
    if (changed === 0) return S.sum_themes_auto;
    return custom === 0 ? S.sum_themes({ changed, total }) : S.sum_themes_custom({ changed, total, custom });
  }

  /** The EFFECTS summary: the slots that wear an effect and the grants the
   *  account holds. None until the grants have answered. */
  function effectsSum(): string {
    const a = ctx.data.cell<AccountEffects>(EFFECTS_CELL, TTL_ON_OPEN, loadEffects).value;
    if (a === undefined) return '';
    const worn = (EFFECT_SLOTS as readonly string[]).filter((s) => pickOf(s) !== '').length;
    return S.sum_effects({ worn, held: a.granted.length });
  }

  /** The WORKSHOP summary: the key it needs, or the categories the maker
   *  made a theme in and their theme links. None until the list answers. */
  function workshopSum(): string {
    if (!hasKey(ctx.me(), KEY_THEME_MAKER)) return S.sum_workshop_locked;
    const m = ctx.data.cell<MyThemes>(MINE_CELL, TTL_ON_OPEN, getMyThemes).value;
    if (m === undefined) return '';
    if (m.themes.length === 0) return S.sum_workshop_none;
    const made = new Set(m.themes.map((t) => t.category)).size;
    const links = m.themes.reduce((n, t) => n + t.links, 0);
    return S.sum_workshop({ made, total: THEME_CATEGORIES.length, links });
  }

  /** The four sections as the rail and the tiles draw them. */
  function sections(): ShellSection[] {
    return [
      { id: 'themes', glyph: '◐', name: S.sec_themes, sum: themesSum() },
      { id: 'effects', glyph: '✧', name: S.sec_effects, sum: effectsSum() },
      { id: 'workshop', glyph: '⌘', name: S.sec_workshop, sum: workshopSum() },
      { id: 'designer', glyph: '▦', name: S.sec_designer, sum: S.sum_designer },
    ];
  }

  /** The whole panel: the shell with the open page and the preview. */
  function draw(wide: boolean, open: ReturnType<typeof nav.shown>): string {
    return shellHtml({
      title: S.title,
      heading: h1(S.title, S.title_sub),
      go: GO,
      back: BACK,
      sections: sections(),
      open,
      wide,
      page: sectionPage(open ?? 'themes'),
      rootFoot: section(S.preview_label, '', previewBlock('')),
    });
  }

  /** The caption of the preview: the selected row and its value, else the
   *  look in force. */
  function caption(): string {
    if (eslot !== null) {
      const v = effectValues(eslot).find((x) => x.code === shownEffect(eslot!));
      return S.preview_pick({ what: slotLabel(eslot), value: v?.label ?? S.value_none });
    }
    if (whole !== null) {
      const same = wholeLevelOf(settingsNow(), wholeLevels());
      return S.preview_pick({ what: S.whole_look, value: same === null ? S.whole_mixed : levelWord(same) });
    }
    if (selected !== null) {
      const s = settingsNow();
      const list = stepValues(selected);
      const what = CATEGORY_WORDS.get(selected)![0];
      return S.preview_pick({ what, value: labelOf(s, selected, shownId(s, selected), list) });
    }
    return S.preview_look;
  }

  /** The preview block: the slot the canvas host moves into, the caption
   *  and the hint of the page. Both the column and the band draw it; the
   *  column adds the slot of the close-up under it (R1914). */
  function previewBlock(hint: string, close = false): string {
    return '<div class="mpreview"><div class="mframe" data-preview-slot></div>'
      + `<div class="mcap"><b>${esc(caption())}</b>`
      + (hint === '' ? '' : `<span class="sd">${esc(hint)}</span>`)
      + '</div>'
      + (close ? '<div class="mclose"><div class="mframe" data-preview-close></div>'
        + `<div class="mcap"><b>${esc(S.preview_close)}</b></div></div>` : '')
      + '</div>';
  }

  /** PREVIEW as the companion of a section. A wide pane with no room for
   *  the column draws it as a band over the page; a telephone pins it over
   *  the pages where a press moves it. The band and the telephone hold the
   *  full preview alone. */
  function previewCompanion(hint: string, narrow: Fold): Companion {
    return {
      label: S.preview_label, html: previewBlock(hint, true), folded: previewBlock(hint), fold: 'top', narrow,
    };
  }

  /** The page of one section. */
  function sectionPage(id: ModsSection): ShellPage {
    switch (id) {
      case 'themes':
        return { body: useCard(), mark: useMark(), companion: previewCompanion(S.preview_hint, 'top') };
      case 'effects': {
        const companion = previewCompanion(S.preview_hint_effects, 'top');
        if (fitsPage()) {
          // WHAT FITS on a narrow pane (R1919): the sub-page of EFFECTS
          // under the head ‹ EFFECTS. The list carries its own head, as on
          // a wide pane, and no box (R1945); the page scrolls in the pane,
          // and nothing scrolls inside it.
          return { body: plain(fitsHtml(eslot!)), sub: { name: slotLabel(eslot!), back: 'efx-back' }, companion };
        }
        return { body: effectsCard(), companion };
      }
      case 'workshop':
        return { body: makeCard(), mark: makeMark(), companion: previewCompanion('', 'none') };
      case 'designer':
        return { body: designerCard(), companion: previewCompanion('', 'none') };
    }
  }

  /** A change of page clears the selected row: it no longer stands, so the
   *  sample stops and the preview shows the look in force. */
  function deselect(): void {
    const had = selected !== null || field !== null || eslot !== null || whole !== null;
    selected = null;
    field = null;
    eslot = null;
    whole = null;
    sampleMusic(false);
    if (had) showPreview();
  }

  /**
   * Puts the preview host into the slot that shows. The shell writes the
   * column, the band and, on a narrow root, the card under the tiles; the
   * stylesheet shows one. A slot with no box shows nothing, and where no
   * slot shows (a pane not laid out), the first slot takes the host.
   * `make` is false for a move alone: a resize never builds a preview.
   */
  function place(root: HTMLElement, make: boolean): void {
    const slots = [...root.querySelectorAll<HTMLElement>('[data-preview-slot]')];
    const slot = slots.find((s) => s.getClientRects().length > 0) ?? slots[0];
    if (slot !== undefined) {
      let fresh = false;
      if (!host && make) {
        host = document.createElement('div');
        preview = makePreview(host, false);
        fresh = true;
      }
      if (host && host.parentElement !== slot) slot.append(host);
      if (fresh && preview) showPreview(preview);
    }
    placeClose(root);
  }

  /**
   * Puts the close-up into its slot in the column while that slot shows,
   * and takes it down while it does not (R1914): a hidden slot keeps no
   * engine. The full preview stands first, so it keeps the chrome ids. A
   * resize builds the close-up, because the column that shows is its one
   * cause.
   */
  function placeClose(root: HTMLElement): void {
    const near = [...root.querySelectorAll<HTMLElement>('[data-preview-close]')]
      .find((s) => s.getClientRects().length > 0);
    if (near === undefined || preview === null) {
      dropClose();
      return;
    }
    let fresh = false;
    if (!closeHost) {
      closeHost = document.createElement('div');
      closePreview = makePreview(closeHost, true);
      fresh = true;
    }
    if (closeHost.parentElement !== near) near.append(closeHost);
    if (fresh && closePreview) showPreview(closePreview);
  }

  return {
    key: 'mods',

    open(): void {
      // The workshop is another tab, so the list is read again on each visit.
      ctx.data.drop(MINE_CELL);
      // An operator grants an effect while the page stands open, so the
      // grants are read again on each visit too.
      ctx.data.drop(EFFECTS_CELL);
      // MIX BY CATEGORY starts open on each visit (R2154). No hub fold
      // keeps its state past a visit, so no device key keeps this one.
      mixOpen = null;
      nav.open();
    },

    close(): void {
      takeDown();
      nav.close();
    },

    destroy(): void { takeDown(); },

    openSection(word: string): void {
      nav.ask(word);
    },

    html(): string {
      const wide = ctx.wide();
      const open = nav.shown(wide);
      drawn = loadSettings();
      try {
        return draw(wide, open);
      } finally {
        drawn = null;
      }
    },

    act(action: string, el: HTMLElement): void {
      if (shellPress(nav, ctx, SHELL, action, el, deselect)) return;
      const c = el.dataset.cat;
      switch (action) {
        case 'go-shop':
          // The press cue, then the tab cue of the hub, as every shop press
          // of the hub plays them (audio.md §4.7).
          ctx.play('sfx-menu-press');
          ctx.go('shop');
          return;
        case 'sel':
          if (isCategory(c)) { select(c); ctx.refresh(); }
          return;
        case 'mix':
          // A fold that shuts drops the row it hid, so the caption and the
          // preview name no row the page does not show.
          mixOpen = !mixShown();
          if (!mixOpen && (selected !== null || field !== null)) {
            selected = null;
            field = null;
            sampleMusic(false);
            showPreview();
          }
          ctx.play('sfx-menu-press');
          ctx.refresh();
          return;
        case 'pick':
          if (isCategory(c) && el.dataset.id !== undefined) choose(c, el.dataset.id);
          return;
        case 'whole':
          if (el.dataset.id !== undefined) chooseWhole(el.dataset.id);
          return;
        case 'whole-sel':
          // The head of THE WHOLE LOOK moves the preview to the wide frame.
          holdWhole(whole ?? '');
          return;
        case 'code':
          if (isCategory(c)) void submit(c);
          return;
        case 'unset-code':
          if (isCategory(c)) unset(c);
          return;
        case 'change-code':
          if (isCategory(c)) {
            rs[c].editing = true;
            rs[c].error = '';
            rs[c].typed = prefill(loadSettings(), c);
            ctx.play('sfx-menu-press');
            ctx.refresh();
          }
          return;
        case 'field':
          if (isCategory(c)) { select(c, el.dataset.field ?? null); ctx.refresh(); }
          return;
        case 'cue':
          if (el.dataset.id) ctx.play(el.dataset.id as SfxId);
          return;
        case 'esel':
          if (el.dataset.slot) {
            // On a narrow pane the press opens WHAT FITS as a sub-page, and
            // the slot map keeps the place the reader left (R1919).
            if (!ctx.wide() && lastEffects() !== null) {
              nav.enter();
              ctx.play('sfx-menu-press');
            }
            eselect(el.dataset.slot);
            ctx.refresh();
          }
          return;
        case 'ewear':
          if (el.dataset.slot && el.dataset.code !== undefined) ewear(el.dataset.slot, el.dataset.code);
          return;
        case 'efx-more':
          fitsAll = !fitsAll;
          ctx.play('sfx-menu-press');
          ctx.refresh();
          return;
        case 'efx-back':
          // ‹ EFFECTS: back to the slot map at the place the reader left.
          nav.leave();
          deselect();
          ctx.play('sfx-menu-back');
          ctx.refresh();
          return;
        default:
      }
    },

    wire(root: HTMLElement): void {
      nav.scroll(root);
      watch?.disconnect();
      watch = null;
      // The pictures go into their boxes. The baker takes the keys of the
      // page in the page order, so the open page bakes first and a key no
      // page shows is dropped (R1918). Its frame is bare and takes no
      // chrome id, so it starts before the panel's own preview.
      lastRoot = root;
      fillPictures(root);
      if (bakery !== null) {
        bakery.only([...root.querySelectorAll<HTMLElement>('[data-pic]')].map((el) => el.dataset.pic ?? ''));
        bakery.start();
      }
      place(root, true);
      // The container query swaps the column and the band without a new
      // render, so a change of the row's width moves the host again.
      const row = root.querySelector<HTMLElement>('.hs-row');
      if (row !== null && typeof ResizeObserver === 'function') {
        watch = new ResizeObserver(() => place(root, false));
        watch.observe(row);
      }
      for (const c of THEME_CATEGORIES) {
        const box = root.querySelector<HTMLInputElement>(`input[data-in="themecode-${c}"]`);
        // A box the rewrite kept holds its listeners and the reader's
        // text already (the review of 2026-10-14, item 20).
        if (!box || !wireOnce(box)) continue;
        box.value = rs[c].typed;
        box.addEventListener('input', () => { rs[c].typed = box.value.toUpperCase(); });
        box.addEventListener('keydown', (ev) => {
          if (matches(KEYS.hub.submit, ev)) void submit(c);
          if (matches(KEYS.hub.cancel, ev)) {
            rs[c].editing = false;
            rs[c].shown = undefined;
            ctx.play('sfx-menu-back');
            ctx.refresh();
          }
        });
      }
    },
  };
}
